import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ImagePlus, X, Loader2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export function PhotoGallery({ entityType, entityId }) {
  const { language } = useLanguage();
  const tf = (fr, en) => language === 'fr' ? fr : en;
  const fileRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadPhotos = async () => {
    try {
      const { data } = await api.get(`/photos/list/${entityType}/${entityId}`);
      setPhotos(data.photos);
    } catch { /* empty */ }
  };

  useEffect(() => { if (entityId) loadPhotos(); }, [entityId]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('entity_type', entityType);
      formData.append('entity_id', entityId);
      for (const f of files) formData.append('files', f);

      await api.post('/photos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(tf('Photos ajoutees', 'Photos added'));
      loadPhotos();
    } catch {
      toast.error(tf('Erreur upload', 'Upload error'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (photoId) => {
    setDeletingId(photoId);
    try {
      await api.delete(`/photos/${photoId}`);
      setPhotos(prev => prev.filter(p => p.photo_id !== photoId));
      toast.success(tf('Photo supprimee', 'Photo deleted'));
    } catch {
      toast.error(tf('Erreur', 'Error'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Camera className="h-5 w-5" />
            Photos ({photos.length})
          </CardTitle>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              multiple
              className="hidden"
              onChange={handleUpload}
              data-testid="photo-file-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="add-photos-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-2" />}
              {tf('Ajouter des photos', 'Add photos')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {photos.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Camera className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">{tf('Aucune photo', 'No photos')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3" data-testid="photo-grid">
            {photos.map(photo => (
              <div key={photo.photo_id} className="relative group aspect-square rounded-lg overflow-hidden border bg-muted">
                <img
                  src={`${window.location.origin}${photo.url}`}
                  alt={photo.original_name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <button
                  onClick={() => handleDelete(photo.photo_id)}
                  disabled={deletingId === photo.photo_id}
                  className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/60 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`delete-photo-${photo.photo_id}`}
                >
                  {deletingId === photo.photo_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[10px] px-1.5 py-0.5 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                  {photo.original_name}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
